import type { ObraDetail, ReportMedia, ReportSummary } from '@obras/shared';
import {
  formatCoordinate,
  formatCurrency,
  formatCurrencyDelta,
  formatDate,
  formatDateTime,
  formatDays,
  formatDuration,
  ratio,
  REPORT_TYPE_META,
} from '@obras/shared';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  ChartLine,
  Image as ImageIcon,
  Images,
  PauseCircle,
  PlayCircle,
  Play,
  Undo2,
  Video,
} from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardHeader, Kpi } from '../../components/ui/Card.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { EmptyState, ErrorState, ProgressBar, Skeleton } from '../../components/ui/Feedback.js';
import { Lightbox, useLightbox, type LightboxItem } from '../../components/ui/Lightbox.js';
import { Modal } from '../../components/ui/Modal.js';
import { Tab, TabList } from '../../components/ui/Tabs.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { cn } from '../../lib/cn.js';
import {
  useEndStoppage,
  useObraLedger,
  useReverseMovement,
  useStartStoppage,
  useStoppages,
} from './api.js';
import { VideoEmEspera } from './VideoEstado.js';

const ICON = { strokeWidth: 1.5 } as const;

const DELTA_TONE: Record<string, string> = {
  muted: 'text-muted-foreground',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

// ────────────────────────────────────────────────────────── financeiro

export function ObraFinanceTab({ obra }: { obra: ObraDetail }) {
  const { can } = useAuth();
  const { data, isPending, isError, error, refetch } = useObraLedger(obra.id);
  const reverse = useReverseMovement(obra.id);

  const [target, setTarget] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reverseError, setReverseError] = useState<string | null>(null);

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-28 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro desconhecido.'}
        onRetry={() => void refetch()}
      />
    );
  }

  const paidRatio = ratio(data.paidValueCents, data.currentValueCents);
  const glossRatio = ratio(data.glossValueCents, data.currentValueCents);

  async function handleReverse(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!target) return;

    setReverseError(null);
    try {
      await reverse.mutateAsync({ movementId: target, reason });
      setTarget(null);
      setReason('');
    } catch (err) {
      setReverseError(
        err instanceof ApiError ? err.message : 'Não foi possível estornar o lançamento.',
      );
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Valor inicial" value={formatCurrency(data.initialValueCents)} />
        <Kpi
          label="Aditivo"
          value={formatCurrencyDelta(data.additionsCents)}
          tone={data.additionsCents > 0 ? 'primary' : 'default'}
        />
        <Kpi
          label="Supressão"
          value={formatCurrencyDelta(-data.suppressionsCents)}
          tone={data.suppressionsCents > 0 ? 'danger' : 'default'}
        />
        <Kpi
          label="Valor final total"
          value={formatCurrency(data.currentValueCents)}
          tone="inverted"
        />
      </div>

      <Card>
        <CardHeader
          title="Progresso de pagamento"
          description="Calculado sobre o valor final do contrato."
        />
        <div className="px-5 pt-1 pb-5">
          <ProgressBar value={paidRatio} label="Pago" tone="success" />

          <div className="tabular mt-3 flex flex-wrap justify-between gap-2 text-[13px]">
            <span className="flex items-center gap-1.5 text-success">
              <span className="size-2 rounded-xs bg-success" aria-hidden />
              Pago: {formatCurrency(data.paidValueCents)}
            </span>

            {data.glossValueCents > 0 ? (
              <span className="flex items-center gap-1.5 text-warning">
                <span className="size-2 rounded-xs bg-warning" aria-hidden />
                Glosa: {formatCurrency(data.glossValueCents)} ({(glossRatio * 100).toFixed(1)}%)
              </span>
            ) : null}

            <span className="text-muted-foreground">
              Saldo: {formatCurrency(data.remainingCents)} de{' '}
              {formatCurrency(data.currentValueCents)}
            </span>
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <CardHeader
          title="Livro-razão"
          description="Todo lançamento é permanente. Correção é estorno, nunca exclusão."
        />

        {data.movements.length === 0 ? (
          <EmptyState
            icon={ChartLine}
            title="Nenhuma movimentação"
            description="Medições, aditivos, supressões e glosas aparecem aqui assim que forem registrados."
          />
        ) : (
          <table className="w-full min-w-[880px] text-left">
            <caption className="sr-only">Movimentações financeiras e de prazo da obra</caption>
            <thead className="bg-muted">
              <tr className="text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                <th scope="col" className="px-5 py-3">
                  Data
                </th>
                <th scope="col" className="px-5 py-3">
                  Movimentação
                </th>
                <th scope="col" className="px-5 py-3">
                  Relatório
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Variação
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Saldo do contrato
                </th>
                <th scope="col" className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.movements.map((movement) => {
                const meta = REPORT_TYPE_META[movement.type];
                const isReversal = movement.reversalOfId !== null;
                const isReversed = movement.reversedByMovementId !== null;

                return (
                  <tr
                    key={movement.id}
                    className={cn(
                      'border-t border-border',
                      (isReversal || isReversed) && 'bg-muted',
                    )}
                  >
                    <td className="tabular px-5 py-3 font-mono text-[13px]">
                      {formatDate(movement.effectiveDate)}
                    </td>

                    <td className="px-5 py-3">
                      <span className={cn('font-medium', DELTA_TONE[meta.tone])}>{meta.label}</span>
                      {isReversal ? (
                        <Badge tone="muted" icon={Undo2} className="ml-2">
                          Estorno
                        </Badge>
                      ) : null}
                      {isReversed ? (
                        <Badge tone="muted" className="ml-2">
                          Estornado
                        </Badge>
                      ) : null}
                    </td>

                    <td className="px-5 py-3 text-[13px] text-muted-foreground">
                      <span className="block truncate">{movement.reportTitle ?? '—'}</span>
                      {movement.authorName ? (
                        <span className="block text-[11px] text-muted-foreground">
                          {movement.authorName}
                        </span>
                      ) : null}
                    </td>

                    <td
                      className={cn(
                        'tabular px-5 py-3 text-right font-semibold',
                        DELTA_TONE[meta.tone],
                        isReversed && 'line-through opacity-60',
                      )}
                    >
                      {movement.days !== null
                        ? `${movement.days > 0 ? '+' : ''}${formatDays(movement.days)}`
                        : formatCurrencyDelta(movement.amountCents)}
                    </td>

                    <td className="tabular px-5 py-3 text-right text-[13px]">
                      {formatCurrency(movement.newValueCents)}
                    </td>

                    <td className="px-5 py-3 text-right">
                      {can('gestor') && !isReversal && !isReversed ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Undo2 {...ICON} size={14} />}
                          onClick={() => setTarget(movement.id)}
                        >
                          Estornar
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Modal
        open={target !== null}
        onClose={() => setTarget(null)}
        title="Estornar movimentação"
        description="O lançamento original permanece no razão, com um estorno ao lado."
        footer={
          <>
            <Button variant="ghost" onClick={() => setTarget(null)} disabled={reverse.isPending}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              form="reverse-form"
              type="submit"
              loading={reverse.isPending}
              icon={<Undo2 {...ICON} size={16} />}
            >
              Confirmar estorno
            </Button>
          </>
        }
      >
        <form
          id="reverse-form"
          onSubmit={(event) => void handleReverse(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {reverseError ? (
            <div
              role="alert"
              className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger"
            >
              {reverseError}
            </div>
          ) : null}

          <TextAreaField
            label="Motivo do estorno"
            required
            rows={4}
            hint="Fica registrado na auditoria. Mínimo de 10 caracteres."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
}

// ────────────────────────────────────────────────────────── galeria

/** O que a galeria mostra. */
type FiltroDeMidia = 'tudo' | 'imagem' | 'video';

const FILTROS: readonly { valor: FiltroDeMidia; rotulo: string; icone: LucideIcon }[] = [
  { valor: 'tudo', rotulo: 'Tudo', icone: Images },
  { valor: 'imagem', rotulo: 'Fotos', icone: ImageIcon },
  { valor: 'video', rotulo: 'Vídeos', icone: Video },
];

/**
 * O acervo visual da obra.
 *
 * Junta foto e vídeo de todos os relatórios num lugar só — é onde se responde
 * "como estava a obra em maio?" sem abrir relatório por relatório. Daí os dois
 * controles: o filtro, para quem procura só o vídeo daquela vistoria, e a
 * ordem, porque conferir o histórico pede o mais antigo primeiro enquanto
 * acompanhar o andamento pede o contrário.
 */
export function ObraGalleryTab({ reports }: { reports: readonly ReportSummary[] }) {
  const lightbox = useLightbox();
  const [filtro, setFiltro] = useState<FiltroDeMidia>('tudo');
  const [recentesPrimeiro, setRecentesPrimeiro] = useState(true);

  /*
   * A data que ordena é a da foto, quando o aparelho a gravou, e só na falta
   * dela a da visita. Vinte fotos do mesmo relatório têm a mesma data de visita
   * e ficariam numa ordem qualquer; a do arquivo devolve a sequência real do
   * percurso pela obra.
   */
  const acervo = useMemo(() => {
    const itens = reports.flatMap((report) =>
      report.media
        .filter((item) => item.kind === 'imagem' || item.kind === 'video')
        .map((item) => ({
          ...item,
          reportTitle: report.title,
          visitDate: report.visitDate,
          quando: item.takenAt ?? report.visitDate,
        })),
    );

    return itens.sort((a, b) =>
      recentesPrimeiro ? b.quando.localeCompare(a.quando) : a.quando.localeCompare(b.quando),
    );
  }, [reports, recentesPrimeiro]);

  const contagem = {
    tudo: acervo.length,
    imagem: acervo.filter((item) => item.kind === 'imagem').length,
    video: acervo.filter((item) => item.kind === 'video').length,
  };

  const visiveis = acervo.filter((item) => filtro === 'tudo' || item.kind === filtro);

  const slides: LightboxItem[] = visiveis.map((item) => ({
    id: item.id,
    url: item.url,
    title: item.reportTitle,
    kind: item.kind === 'video' ? 'video' : 'imagem',
    thumbnailUrl: item.thumbnailUrl,
    meta: [
      formatDateTime(item.takenAt) !== '—'
        ? formatDateTime(item.takenAt)
        : formatDate(item.visitDate),
      item.durationSeconds ? formatDuration(item.durationSeconds) : null,
      item.latitude != null && item.longitude != null
        ? `${formatCoordinate(item.latitude, 'lat')} ${formatCoordinate(item.longitude, 'lng')}`
        : null,
    ].filter((line): line is string => Boolean(line)),
  }));

  // Acervo vazio é outra coisa de filtro vazio: um pede envio, o outro, ajuste.
  if (acervo.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Images}
          title="Nenhuma foto ou vídeo"
          description="As fotos e os vídeos enviados nos relatórios de visita aparecem aqui, com data e coordenada quando disponíveis."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabList label="Filtrar a galeria">
          {FILTROS.map((item) => (
            <Tab
              key={item.valor}
              active={filtro === item.valor}
              onClick={() => setFiltro(item.valor)}
              icon={item.icone}
              count={contagem[item.valor]}
              disabled={contagem[item.valor] === 0 && item.valor !== 'tudo'}
            >
              {item.rotulo}
            </Tab>
          ))}
        </TabList>

        <Button
          size="sm"
          onClick={() => setRecentesPrimeiro((atual) => !atual)}
          icon={
            recentesPrimeiro ? (
              <ArrowDownWideNarrow {...ICON} size={15} />
            ) : (
              <ArrowUpNarrowWide {...ICON} size={15} />
            )
          }
        >
          {recentesPrimeiro ? 'Mais recentes' : 'Mais antigos'}
        </Button>
      </div>

      {visiveis.length === 0 ? (
        <Card>
          <EmptyState
            icon={filtro === 'video' ? Video : ImageIcon}
            title={filtro === 'video' ? 'Nenhum vídeo' : 'Nenhuma foto'}
            description="Nenhum item deste tipo foi enviado nos relatórios desta obra."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {visiveis.map((item, position) => (
            <GalleryItem
              key={item.id}
              item={item}
              legenda={formatDate(item.visitDate)}
              onOpen={() => lightbox.open(position)}
            />
          ))}
        </div>
      )}

      <Lightbox
        items={slides}
        index={lightbox.index}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
      />
    </div>
  );
}

/** Um quadro da galeria. Vídeo ganha play, duração e os estados do Bunny. */
function GalleryItem({
  item,
  legenda,
  onOpen,
}: {
  item: ReportMedia & { reportTitle: string };
  legenda: string;
  onOpen: () => void;
}) {
  const video = item.kind === 'video';

  if (video && item.status && item.status !== 'pronto') {
    return <VideoEmEspera mediaId={item.id} status={item.status} />;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Ver ${item.reportTitle}`}
      className="group relative aspect-square overflow-hidden rounded-sm bg-muted"
    >
      <img
        src={video ? item.thumbnailUrl : item.url}
        alt=""
        loading="lazy"
        className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
      />

      {video ? (
        <>
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-11 place-items-center rounded-full bg-black/55 text-white transition-colors group-hover:bg-black/70">
              <Play size={17} strokeWidth={2} fill="currentColor" aria-hidden />
            </span>
          </span>
          {item.durationSeconds ? (
            <span className="tabular absolute top-1.5 right-1.5 rounded-xs bg-black/65 px-1.5 py-0.5 text-[11px] font-medium text-white">
              {formatDuration(item.durationSeconds)}
            </span>
          ) : null}
        </>
      ) : null}

      <span className="absolute inset-x-0 bottom-0 truncate bg-[rgba(9,9,11,0.6)] px-2 py-1 text-left font-mono text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100">
        {legenda}
      </span>
    </button>
  );
}

// ────────────────────────────────────────────────────── paralisações

export function ObraStoppagesTab({ obra }: { obra: ObraDetail }) {
  const { can } = useAuth();
  const { data, isPending, isError, error, refetch } = useStoppages(obra.id);
  const start = useStartStoppage(obra.id);
  const end = useEndStoppage(obra.id);

  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [startedAt, setStartedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [endedAt, setEndedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const active = data?.find((item) => item.endedAt === null);
  const canWrite = can('fiscal');

  if (isPending) return <Skeleton className="h-48 rounded-lg" />;
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro desconhecido.'}
        onRetry={() => void refetch()}
      />
    );
  }

  async function submitStart(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    try {
      await start.mutateAsync({
        startedAt,
        reason,
        description: description.trim() === '' ? undefined : description,
      });
      setStartOpen(false);
      setReason('');
      setDescription('');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Não foi possível paralisar a obra.');
    }
  }

  async function submitEnd(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    try {
      await end.mutateAsync({ endedAt, resumeStatus: 'em_andamento' });
      setEndOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Não foi possível retomar a obra.');
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {active ? (
        <Card className="bg-paused-bg p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-medium text-paused">
                <PauseCircle {...ICON} size={18} aria-hidden />
                Obra paralisada há {formatDays(active.durationDays)}
              </p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Desde {formatDate(active.startedAt)} · {active.reason}
              </p>
            </div>

            {canWrite ? (
              <Button
                variant="success"
                icon={<PlayCircle {...ICON} size={16} />}
                onClick={() => setEndOpen(true)}
              >
                Retomar obra
              </Button>
            ) : null}
          </div>
        </Card>
      ) : (
        <div className="flex justify-end">
          {canWrite && obra.status !== 'concluida' && obra.status !== 'cancelada' ? (
            <Button
              variant="secondary"
              icon={<PauseCircle {...ICON} size={16} />}
              onClick={() => setStartOpen(true)}
            >
              Registrar paralisação
            </Button>
          ) : null}
        </div>
      )}

      {data.length === 0 ? (
        <Card>
          <EmptyState
            icon={PauseCircle}
            title="Nenhuma paralisação registrada"
            description="O histórico de paralisações desta obra aparece aqui, com o tempo total parado."
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-border">
            {data.map((stoppage) => (
              <li key={stoppage.id} className="flex flex-wrap items-start gap-3 px-5 py-4">
                <PauseCircle
                  {...ICON}
                  size={18}
                  className={cn(
                    'mt-0.5 shrink-0',
                    stoppage.endedAt ? 'text-muted-foreground' : 'text-paused',
                  )}
                  aria-hidden
                />

                <div className="min-w-0 flex-1">
                  <p className="font-medium">{stoppage.reason}</p>
                  {stoppage.description ? (
                    <p className="mt-1 text-[13px] text-muted-foreground">{stoppage.description}</p>
                  ) : null}
                  <p className="tabular mt-1 font-mono text-[11px] text-muted-foreground">
                    {formatDate(stoppage.startedAt)} →{' '}
                    {stoppage.endedAt ? formatDate(stoppage.endedAt) : 'em vigor'}
                    {stoppage.createdBy ? ` · ${stoppage.createdBy.name}` : ''}
                  </p>
                </div>

                <Badge tone={stoppage.endedAt ? 'muted' : 'paused'}>
                  {formatDays(stoppage.durationDays)}
                </Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Modal
        open={startOpen}
        onClose={() => setStartOpen(false)}
        title="Registrar paralisação"
        description="A obra passa para a situação Paralisada até ser retomada."
        footer={
          <>
            <Button variant="ghost" onClick={() => setStartOpen(false)} disabled={start.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="stoppage-form"
              type="submit"
              loading={start.isPending}
              icon={<PauseCircle {...ICON} size={16} />}
            >
              Paralisar obra
            </Button>
          </>
        }
      >
        <form
          id="stoppage-form"
          onSubmit={(event) => void submitStart(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {formError ? (
            <div
              role="alert"
              className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger"
            >
              {formError}
            </div>
          ) : null}

          <TextField
            label="Início da paralisação"
            type="date"
            mono
            required
            value={startedAt}
            onChange={(event) => setStartedAt(event.target.value)}
          />

          <TextField
            label="Motivo"
            required
            placeholder="Chuvas intensas, embargo, falta de material…"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />

          <TextAreaField
            label="Detalhamento"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </form>
      </Modal>

      <Modal
        open={endOpen}
        onClose={() => setEndOpen(false)}
        title="Retomar obra"
        description="A obra volta para Em andamento a partir da data informada."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEndOpen(false)} disabled={end.isPending}>
              Cancelar
            </Button>
            <Button
              variant="success"
              form="resume-form"
              type="submit"
              loading={end.isPending}
              icon={<PlayCircle {...ICON} size={16} />}
            >
              Retomar
            </Button>
          </>
        }
      >
        <form
          id="resume-form"
          onSubmit={(event) => void submitEnd(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          {formError ? (
            <div
              role="alert"
              className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger"
            >
              {formError}
            </div>
          ) : null}

          <TextField
            label="Fim da paralisação"
            type="date"
            mono
            required
            value={endedAt}
            onChange={(event) => setEndedAt(event.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
}
